import { getVaultStatus } from '../offline/vault';
import React, { useCallback, useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation } from 'react-router-dom';
import { exportChartDraft, listChartDrafts, readChartDraft, removeChartDraft, saveChartDraft, updateChartDraft } from '../offline/chartDraftStore';
import { getAllRequests, getRawRequestRecords } from '../offline/offlineQueue';
import { syncChartDrafts, syncReviewedConflict } from '../offline/chartSync';
import { inspectLegacyClinicalRequest, resolveLegacyClinicalContext } from '../offline/legacyChartRecovery';
import { replayQueuedRequests } from '../offline/fetchProxy';
import { shouldAutoReplayQueuedRequest, shouldDiscardQueuedRequest } from '../offline/config';
import { countDeviceDrafts, OPEN_DEVICE_DRAFTS } from '../offline/deviceDraftStatus';

const LABELS = {
  'saved-on-device': 'Saved on device', syncing: 'Syncing', synced: 'Synced',
  conflict: 'Conflict', 'action-required': 'Action required', 'legacy-review': 'Review required',
  'recovery-only': 'Saved on this device — review required',
};

export function readableDraftError(errorMessage) {
  if (!errorMessage || typeof errorMessage !== 'string') return errorMessage;
  try {
    const parsed = JSON.parse(errorMessage);
    return parsed?.error || parsed?.message || errorMessage;
  } catch {
    return errorMessage;
  }
}

export default function OfflineChartWorkspace() {
  const { pathname } = useLocation();
  const [open, setOpen] = useState(false);
  const [drafts, setDrafts] = useState([]);
  const [pendingRequestCount, setPendingRequestCount] = useState(0);
  const [legacyCount, setLegacyCount] = useState(0);
  const [legacyItems, setLegacyItems] = useState([]);
  const [legacyInspectionBusy, setLegacyInspectionBusy] = useState(false);
  const [legacyInspectionError, setLegacyInspectionError] = useState('');
  const [confirmRecoveryDraftId, setConfirmRecoveryDraftId] = useState(null);
  const [confirmConflictDraftId, setConfirmConflictDraftId] = useState(null);
  const [confirmDiscardDraftId, setConfirmDiscardDraftId] = useState(null);
  const [retryBusy, setRetryBusy] = useState(false);
  const [retryError, setRetryError] = useState('');
  const [noticeDismissed, setNoticeDismissed] = useState(false);
  const [retainedDetails, setRetainedDetails] = useState({});

  const closeWorkspace = useCallback(() => {
    setOpen(false);
    setConfirmRecoveryDraftId(null);
    setConfirmConflictDraftId(null);
    setConfirmDiscardDraftId(null);
  }, []);

  const refresh = useCallback(async () => {
    // Locked encrypted requests are not unidentified legacy records.
    const vault = await getVaultStatus();
    if (vault.enrolled && !vault.unlocked) { setLegacyCount(0); setPendingRequestCount(0); setDrafts([]); return; }
    const [nextDrafts, rawRequests, decodedRequests] = await Promise.all([
      listChartDrafts().catch(() => []),
      // The database is shared by browser profiles. Only compare decoded
      // requests with raw records owned by the active account and practice.
      getRawRequestRecords({ includeOtherOwners: false }).catch(() => []),
      getAllRequests().catch(() => []),
    ]);
    const replayableCount = decodedRequests.filter((request) => shouldAutoReplayQueuedRequest(request)).length;
    const discardableCount = decodedRequests.filter((request) => shouldDiscardQueuedRequest(request)).length;
    setDrafts(nextDrafts);
    setPendingRequestCount(replayableCount);
    // Undecodable records are conservatively retained as legacy data.
    setLegacyCount(Math.max(0, rawRequests.length - replayableCount - discardableCount));
  }, []);

  useEffect(() => {
    refresh();
    const handleOfflineDataChanged = () => {
      setNoticeDismissed(false);
      refresh();
    };
    window.addEventListener('vela:chart-drafts-changed', handleOfflineDataChanged);
    window.addEventListener('vela:write-queued', handleOfflineDataChanged);
    window.addEventListener('vela:legacy-queue-changed', handleOfflineDataChanged);
    window.addEventListener('vela:offline-vault-changed', handleOfflineDataChanged);
    return () => {
      window.removeEventListener('vela:chart-drafts-changed', handleOfflineDataChanged);
      window.removeEventListener('vela:write-queued', handleOfflineDataChanged);
      window.removeEventListener('vela:legacy-queue-changed', handleOfflineDataChanged);
      window.removeEventListener('vela:offline-vault-changed', handleOfflineDataChanged);
    };
  }, [refresh]);

  useEffect(() => {
    closeWorkspace();
  }, [closeWorkspace, pathname]);

  useEffect(() => {
    if (!open) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === 'Escape') closeWorkspace();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [closeWorkspace, open]);

  const download = async (draft) => {
    const blob = new Blob([await exportChartDraft(draft.draftId)], { type: 'application/json' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `sena-chart-draft-${draft.draftId}.json`;
    link.click();
    URL.revokeObjectURL(link.href);
  };

  const review = async (draft) => {
    if (!draft.clientReferenceId) return;
    if (['legacy-review', 'conflict'].includes(draft.status) && !draft.reviewedAt) {
      await updateChartDraft(draft.draftId, { reviewedAt: new Date().toISOString() });
    }
    sessionStorage.setItem('sena:resume-offline-draft', draft.draftId);
    const target = `/charting/${encodeURIComponent(draft.clientReferenceId)}`;
    if (window.location.pathname === target) {
      window.dispatchEvent(new CustomEvent('vela:resume-offline-draft', { detail: { draftId: draft.draftId } }));
      closeWorkspace();
    } else {
      window.location.assign(target);
    }
  };

  const inspectLegacyItems = async () => {
    setLegacyInspectionBusy(true);
    setLegacyInspectionError('');
    try {
      const decoded = await getAllRequests();
      const clinical = decoded.map(inspectLegacyClinicalRequest).filter(Boolean);
      const resolved = await Promise.all(clinical.map(async item => {
        try { return { ...(await resolveLegacyClinicalContext(item)), resolutionError: null }; }
        catch (error) { return { ...item, resolutionError: error.message }; }
      }));
      setLegacyItems(resolved);
      if (!resolved.length) {
        setLegacyInspectionError('The retained data could not be identified as a supported clinical chart. Keep this browser data intact.');
      }
    } catch (error) {
      setLegacyInspectionError(error.message || 'Legacy data could not be inspected on this device.');
    } finally {
      setLegacyInspectionBusy(false);
    }
  };

  const openWorkspace = () => {
    setNoticeDismissed(false);
    setOpen(true);
    if (legacyCount) inspectLegacyItems();
  };

  useEffect(() => {
    const showDrafts = () => { setNoticeDismissed(false); setOpen(true); refresh(); };
    window.addEventListener(OPEN_DEVICE_DRAFTS, showDrafts);
    return () => window.removeEventListener(OPEN_DEVICE_DRAFTS, showDrafts);
  }, [refresh]);

  const recoverForReview = async (item) => {
    const existing = drafts.find(draft => draft.legacyRequestId === item.id);
    if (existing) {
      await review(existing);
      return;
    }
    const draft = await saveChartDraft({
      url: item.url,
      method: item.method,
      headers: item.headers,
      body: item.body,
      chartType: item.chartType,
      recoveryOnly: item.recoveryOnly === true,
      clientReferenceId: item.clientReferenceId,
      pregnancyReferenceId: item.pregnancyReferenceId,
      pregnancyId: item.pregnancyId,
      chartId: item.chartId,
      baseVersion: item.baseVersion,
      legacyRequestId: item.id,
      status: 'legacy-review',
      errorCategory: 'legacy-review',
      errorMessage: 'Review this recovered chart before approving server recovery.',
    });
    if (item.recoveryOnly) await refresh(); else await review(draft);
  };

  const approveRecovery = async (draft) => {
    if (draft.recoveryOnly) return;
    await updateChartDraft(draft.draftId, {
      status: 'saved-on-device',
      errorCategory: null,
      errorMessage: null,
    });
    setConfirmRecoveryDraftId(null);
    await syncChartDrafts();
    refresh();
  };

  const resolveReviewedConflict = async (draft) => {
    setConfirmConflictDraftId(null);
    try {
      await syncReviewedConflict(draft.draftId);
    } catch (error) {
      await updateChartDraft(draft.draftId, {
        status: 'conflict',
        errorCategory: 'conflict',
        errorMessage: error.message || 'The reviewed draft could not be synchronized.',
      });
    }
    refresh();
  };

  const retryPendingRequests = async () => {
    setRetryBusy(true);
    setRetryError('');
    try {
      const result = await replayQueuedRequests();
      if (result?.failedStatusCodes?.includes(403)) {
        setRetryError('The server denied access to a pending change (403). It is still saved on this device. Check that you are signed in to the correct account and practice before retrying.');
      } else if (result?.failedStatusCodes?.length) {
        setRetryError('The server could not accept a pending change. It is still saved on this device. Try again later or contact support if this continues.');
      } else if (result?.retryable) {
        setRetryError('The connection is still unavailable. Your pending changes remain saved on this device and will retry automatically.');
      }
      await refresh();
    } catch {
      setRetryError('Pending changes could not be retried. Keep this browser data intact and try again.');
    } finally {
      setRetryBusy(false);
    }
  };

  const { pendingCount, syncingCount, reviewCount } = countDeviceDrafts(drafts);
  // A confirmed receipt is history, not outstanding work. Keep it available
  // in an open workspace without leaving a warning badge on the chart.
  const hasOutstanding = pendingCount + reviewCount + legacyCount + pendingRequestCount > 0;
  if (noticeDismissed || (!hasOutstanding && !open)) return null;
  const workspaceLabel = reviewCount > 0
    ? `Device drafts need review (${reviewCount})`
    : syncingCount > 0
    ? `Syncing offline charts (${syncingCount})`
      : pendingCount > 0
      ? `Saved on device (${pendingCount})`
      : pendingRequestCount > 0
        ? `Pending sync (${pendingRequestCount})`
      : drafts.length > 0
        ? `Synced charts to review (${drafts.length})`
        : `Legacy offline data (${legacyCount})`;
  return (
    <>
      {hasOutstanding && <div className="fixed right-3 top-32 z-[55] flex items-center overflow-hidden rounded-full bg-amber-700 text-white shadow-lg">
        <button type="button" onClick={openWorkspace} className="px-3 py-2 text-xs font-semibold">
          {workspaceLabel}
        </button>
        <button type="button" onClick={() => { closeWorkspace(); setNoticeDismissed(true); }} className="border-l border-amber-500 px-2.5 py-2 text-xs font-semibold hover:bg-amber-800 focus:outline-none focus:ring-2 focus:ring-inset focus:ring-white" aria-label="Dismiss offline data notice">✕</button>
      </div>}
      {open && createPortal(<div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4" role="presentation" onClick={closeWorkspace}>
        <section className="max-h-[85vh] w-full max-w-3xl overflow-auto rounded-xl bg-white p-6 shadow-xl" role="dialog" aria-modal="true" aria-labelledby="offline-charts-title" onClick={(e) => e.stopPropagation()}>
          <div className="flex items-start justify-between gap-4">
            <div><h2 id="offline-charts-title" className="text-xl font-semibold">Offline chart workspace</h2><p className="mt-1 text-sm text-gray-600">This workspace protects chart changes that may not have reached the server. Review each item, then sync it or discard it if the server chart is already complete.</p></div>
            <button type="button" onClick={closeWorkspace} className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg text-xl text-gray-600 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#4f7a64]" aria-label="Close offline chart workspace">✕</button>
          </div>
          {reviewCount > 0 && <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950">
            <h3 className="font-semibold">Device drafts need review ({reviewCount})</h3>
            <p className="mt-1">These retained drafts will not sync automatically. Your current chart may already be synced, but these copies may contain other changes. Review them before choosing what to keep.</p>
          </div>}
          {pendingRequestCount > 0 && <div className="mt-4 rounded-lg border border-blue-200 bg-blue-50 p-4 text-sm text-blue-950">
            <h3 className="font-semibold">Pending sync ({pendingRequestCount})</h3>
            <p className="mt-1">A connection interruption delayed {pendingRequestCount === 1 ? 'a change' : 'some changes'}. Sena will retry automatically while this app is open.</p>
            <button type="button" disabled={retryBusy} onClick={retryPendingRequests} className="mt-3 rounded border border-blue-700 px-3 py-1.5 text-xs font-semibold text-blue-800 disabled:opacity-50">{retryBusy ? 'Retrying…' : 'Retry now'}</button>
            {retryError && <p role="alert" className="mt-3 text-red-800">{retryError}</p>}
          </div>}
          {legacyCount > 0 && <div className="mt-4 rounded-lg border border-amber-300 bg-amber-50 p-4 text-sm text-amber-950">
            <h3 className="font-semibold">Legacy offline data detected ({legacyCount})</h3>
            <p className="mt-1">This browser retained data from the previous offline system. Review it locally before deciding whether it should be recovered to the server.</p>
            {legacyInspectionBusy && <p className="mt-3 font-medium">Identifying retained charts…</p>}
            {legacyInspectionError && <p className="mt-3 rounded border border-amber-300 bg-white p-2 text-amber-900">{legacyInspectionError}</p>}
            <div className="mt-3 space-y-2">
              {legacyItems.map(item => <div key={item.id} className="rounded border border-amber-200 bg-white p-3">
                <div className="flex flex-wrap items-start justify-between gap-2">
                  <div><strong className="capitalize">{item.chartType.replace('-', ' ')} chart</strong><div className="text-xs text-gray-600">{item.method === 'POST' ? 'New chart' : 'Chart update'}{item.visitDate ? ` · ${item.visitDate}` : ''}{item.createdAt ? ` · retained ${new Date(item.createdAt).toLocaleString()}` : ''}</div></div>
                  {item.clientReferenceId && !item.resolutionError && <button type="button" onClick={() => recoverForReview(item)} className="rounded bg-[#4f7a64] px-3 py-1.5 text-xs font-semibold text-white">Review recovered data</button>}
                </div>
                {item.recoveryOnly && <details className="mt-2"><summary className="cursor-pointer">Show retained values</summary><RetainedValues value={(() => { try { return JSON.parse(item.body || '{}'); } catch { return item.body; } })()} /></details>}
                {item.resolutionError && <p className="mt-2 text-xs text-red-700">{item.resolutionError}</p>}
              </div>)}
            </div>
            <p className="mt-3 text-xs">Review is read-only. Nothing is sent or deleted until you explicitly approve recovery.</p>
          </div>}
          <div className="mt-4 space-y-3">
            {drafts.map((draft) => <article key={draft.draftId} className="rounded-lg border p-4">
              <div className="flex flex-wrap items-center justify-between gap-2"><div><strong className="capitalize">{draft.chartType.replace('-', ' ')}</strong><div className="text-xs text-gray-500">Last device save: {new Date(draft.locallySavedAt || draft.updatedAt).toLocaleString()}</div></div><span className={`rounded-full px-2 py-1 text-xs font-semibold ${draft.status === 'synced' ? 'bg-green-100 text-green-900' : 'bg-amber-100 text-amber-900'}`}>{LABELS[draft.status] || draft.status}</span></div>
              {draft.status === 'conflict' && <p className="mt-2 rounded border border-amber-200 bg-amber-50 p-3 text-sm text-amber-950">{draft.conflictingVisitId
                ? 'A visit already exists at this date and time. Review this device draft and the existing visit in Past Visits before deciding which details still need to be charted.'
                : 'The server chart was updated after this device draft was saved. If the completed server chart already includes everything you need, discard this device draft. Otherwise, review it before deciding which version to keep.'}</p>}
              {draft.errorMessage && <p className="mt-2 text-sm text-red-700">{readableDraftError(draft.errorMessage)}</p>}
              {draft.syncedAt && <p className="mt-2 text-xs text-green-700">Server confirmed: {new Date(draft.syncedAt).toLocaleString()}</p>}
              {draft.recoveryOnly && <div className="mt-2 text-sm"><p>Your complete editor draft will appear when you reopen that chart section. These retained request values are also available for comparison; they are never sent automatically.</p><button type="button" className="mt-2 underline" onClick={async () => { const saved = await readChartDraft(draft.draftId); let values; try { values = JSON.parse(saved?.request?.body || '{}' ); } catch { values = saved?.request?.body; } setRetainedDetails(previous => ({ ...previous, [draft.draftId]: values })); }}>Show retained values</button>{retainedDetails[draft.draftId] && <RetainedValues value={retainedDetails[draft.draftId]} />}</div>}
              <div className="mt-3 flex flex-wrap gap-2">
                {draft.clientReferenceId && !draft.recoveryOnly && <button type="button" onClick={() => review(draft)} className="rounded bg-[#4f7a64] px-3 py-1.5 text-sm text-white">{draft.status === 'synced' ? 'Review synced chart' : draft.status === 'legacy-review' ? 'Review recovered data' : 'Review draft'}</button>}
                {!draft.recoveryOnly && !['synced', 'legacy-review', 'conflict', 'recovery-only'].includes(draft.status) && <button type="button" onClick={async () => { await syncChartDrafts({ retryActionRequired: true, draftId: draft.draftId }); refresh(); }} className="rounded border border-[#4f7a64] px-3 py-1.5 text-sm text-[#4f7a64]">Retry sync</button>}
                {!draft.recoveryOnly && draft.status === 'conflict' && draft.reviewedAt && (['PUT', 'PATCH'].includes(draft.requestMethod) || (!draft.requestMethod && draft.chartId)) && <button type="button" onClick={() => setConfirmConflictDraftId(draft.draftId)} className="rounded border border-red-700 px-3 py-1.5 text-sm font-semibold text-red-800">Use reviewed draft</button>}
                {!draft.recoveryOnly && draft.status === 'legacy-review' && draft.reviewedAt && <button type="button" onClick={() => setConfirmRecoveryDraftId(draft.draftId)} className="rounded border border-amber-600 px-3 py-1.5 text-sm font-semibold text-amber-800">Recover to server</button>}
                <button type="button" onClick={() => download(draft)} className="rounded border px-3 py-1.5 text-sm">Export encrypted recovery file</button>
                {draft.status === 'synced' && <button type="button" onClick={async () => { await removeChartDraft(draft.draftId); refresh(); }} className="rounded border px-3 py-1.5 text-sm text-gray-600">Remove from list</button>}
                {draft.status !== 'synced' && <button type="button" onClick={() => setConfirmDiscardDraftId(draft.draftId)} className="rounded border px-3 py-1.5 text-sm text-gray-700">Discard device draft</button>}
              </div>
              {confirmRecoveryDraftId === draft.draftId && <div className="mt-3 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-900"><strong>Confirm recovery</strong><p className="mt-1">First verify this visit is not already complete on the server. Recovery will submit the retained chart using duplicate protection.</p><div className="mt-2 flex gap-2"><button type="button" onClick={() => approveRecovery(draft)} className="rounded bg-red-700 px-3 py-1.5 text-white">Confirm recovery</button><button type="button" onClick={() => setConfirmRecoveryDraftId(null)} className="rounded border px-3 py-1.5">Cancel</button></div></div>}
              {confirmConflictDraftId === draft.draftId && <div className="mt-3 rounded border border-red-200 bg-red-50 p-3 text-sm text-red-900"><strong>Use the reviewed device draft?</strong><p className="mt-1">This loads the current server version, then replaces its editable chart fields with the draft you just reviewed. If the server changes again before the save completes, Sena will stop with another conflict.</p><div className="mt-2 flex gap-2"><button type="button" onClick={() => resolveReviewedConflict(draft)} className="rounded bg-red-700 px-3 py-1.5 text-white">Use reviewed draft</button><button type="button" onClick={() => setConfirmConflictDraftId(null)} className="rounded border px-3 py-1.5">Cancel</button></div></div>}
              {confirmDiscardDraftId === draft.draftId && <div className="mt-3 rounded border border-amber-300 bg-amber-50 p-3 text-sm text-amber-950"><strong>Discard this device draft?</strong><p className="mt-1">Only this recovery copy on this device will be removed. The chart currently saved on the server will not change. Make sure the server chart is complete before continuing.</p><div className="mt-2 flex gap-2"><button type="button" onClick={async () => { await removeChartDraft(draft.draftId); setConfirmDiscardDraftId(null); refresh(); }} className="rounded bg-red-700 px-3 py-1.5 text-white">Discard device draft</button><button type="button" onClick={() => setConfirmDiscardDraftId(null)} className="rounded border px-3 py-1.5">Keep draft</button></div></div>}
            </article>)}
          </div>
          <p className="mt-5 text-xs text-gray-500">Do not clear browser storage, uninstall the app, or switch browser profiles while charts are pending.</p>
        </section>
      </div>, document.body)}
    </>
  );
}

function RetainedValues({ value }) {
  if (value == null) return <span>Not recorded</span>;
  if (typeof value !== 'object') return <span className="break-words">{String(value)}</span>;
  return <dl className="mt-2 space-y-2 border-l pl-3">{Object.entries(value).filter(([key]) => !/authorization|token|password/i.test(key)).map(([key, child]) => <div key={key}><dt className="text-xs font-semibold capitalize">{key.replace(/([a-z])([A-Z])/g, '$1 $2')}</dt><dd className="text-sm"><RetainedValues value={child} /></dd></div>)}</dl>;
}
